import React, { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { Alert } from '../../../forms/components/Alert/Alert';
import { AlertLevel } from '../../../forms/components/Alert/props';
import { SMLAnalytics } from '../../../analytics/sml/analytics';
import { useCatalogTrackResolution } from '../common/useCatalogTrackResolution';
import { CatalogTrackResolverProps } from './props';

/**
 * Loads a picked catalog track, keeps the links the catalog already knows and
 * searches only the music services that are missing from it.
 *
 * The work itself lives in `useCatalogTrackResolution`, which the creation
 * sheet also runs. This is the wizard's face for it: one spinner, then the
 * results handed on in a single call.
 */
const CatalogTrackResolverComponent: React.FC<CatalogTrackResolverProps> = ({
  trackMaid,
  onResults,
  onUseUrlInstead,
}) => {
  const intl = useIntl();
  const resolution = useCatalogTrackResolution(trackMaid);
  const { isResolving, error, retry, main, others, title, hasNoLinks } = resolution;
  const hasPublishedRef = useRef(false);

  useEffect(() => {
    if (isResolving || main === undefined || hasPublishedRef.current) {
      return;
    }
    hasPublishedRef.current = true;
    onResults(main, others, trackMaid);
    SMLAnalytics.trackSmartLinkCreateStarted('release', 'track').catch(console.error);
  }, [isResolving, main, onResults, others, trackMaid]);

  const onRetry = useCallback(() => {
    retry();
  }, [retry]);

  if (error) {
    return <ErrorView error={error} retry={onRetry} />;
  }

  if (hasNoLinks) {
    return (
      <>
        <Alert level={AlertLevel.warning} center={false}>
          {intl.formatMessage({
            id: 'app.smart_links.release_smart_link.catalog.no_links',
            defaultMessage: 'We found no music service links for this track. Paste a link to the release instead.',
          })}
        </Alert>
        <SMLButton color="link" onClick={onUseUrlInstead}>
          {intl.formatMessage({
            id: 'app.smart_links.release_smart_link.catalog.use_url',
            defaultMessage: 'Paste a link instead',
          })}
        </SMLButton>
      </>
    );
  }

  return (
    <LoadingView
      label={
        title === undefined
          ? intl.formatMessage({
              id: 'app.smart_links.release_smart_link.catalog.loading_track',
              defaultMessage: 'Loading the release…',
            })
          : intl.formatMessage({
              id: 'app.smart_links.release_smart_link.catalog.loading_links',
              defaultMessage: 'Looking for this release on the other music services…',
            })
      }
    />
  );
};

export const CatalogTrackResolver = TypedMemo(CatalogTrackResolverComponent);
